import type * as echarts from 'echarts';

import type { BridgeStep, DashboardTile, WaterfallBar } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { buildWaterfallOption } from '../../shared/self-service-recompute.utils';

/**
 * Pure helpers for WATERFALL tiles — payload sanitising and the bars → ECharts
 * option / widget-data mapping. Used by the waterfall step at Generate time
 * and by the shell for recompute results, cross-filter re-fetches and the
 * tile Format popup.
 */

/** The wire shape of one bridge step: formula steps carry `formula`, data steps carry dataset/field/aggregation. */
export function sanitizeStepForPayload(step: BridgeStep): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {
    label: step.label,
    stepType: step.stepType,
  };

  if (step.stepType === 'TOTAL' || ((step.stepType === 'CHANGE' || step.stepType === 'START') && step.formulaMode)) {
    sanitized['formula'] = step.formula;
  } else {
    sanitized['dataset'] = step.dataset;
    sanitized['field'] = step.field;
    sanitized['aggregation'] = step.aggregation;
  }

  if (step.stepType === 'CHANGE' && step.sign && step.sign !== 'AUTO') {
    sanitized['sign'] = step.sign;
  }

  return sanitized;
}

/** Stamp each bar with the explicit POSITIVE / NEGATIVE sign of its step (matched by label, else by index). */
export function applyStepSignsToWaterfallBars(bars: WaterfallBar[], payload?: Record<string, unknown>): WaterfallBar[] {
  if (!Array.isArray(bars) || !bars.length) return [];
  const steps = Array.isArray(payload?.['steps']) ? payload['steps'] as Array<Record<string, unknown>> : [];
  if (!steps.length) return bars;

  const normalize = (value: unknown) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  const stepsByLabel = new Map<string, Record<string, unknown>>();
  steps.forEach((step) => {
    const key = normalize(step['label']);
    if (key) stepsByLabel.set(key, step);
  });

  return bars.map((bar, index) => {
    const matchingStep = stepsByLabel.get(normalize(bar.label)) ?? steps[index];
    const sign = String(matchingStep?.['sign'] ?? 'AUTO').toUpperCase();
    return sign === 'AUTO' ? bar : { ...bar, colorSign: sign };
  });
}

export function buildWaterfallPreviewOption(bars: WaterfallBar[], format?: Record<string, unknown>): echarts.EChartsOption {
  return buildWaterfallOption(bars, format) as echarts.EChartsOption;
}

function toAxisLabelRotation(value: unknown, fallback: number): number {
  const rotation = Number(value ?? fallback);
  return Number.isFinite(rotation) ? rotation : fallback;
}

/** Re-apply the Format popup's X-axis label rotation onto an already-built option. */
export function withWaterfallXAxisLabelRotation(option: echarts.EChartsOption | null | undefined, format?: Record<string, unknown>): echarts.EChartsOption | null | undefined {
  if (!option || typeof option !== 'object') return option;
  const rotate = toAxisLabelRotation(format?.['xAxisLabelRotation'], 30);
  const patchAxis = (axis: unknown): unknown => ({
    ...((axis as Record<string, unknown> | undefined) || {}),
    axisLabel: {
      ...(((axis as Record<string, unknown> | undefined)?.['axisLabel'] as Record<string, unknown> | undefined) || {}),
      rotate,
    },
  });
  const next = { ...(option as Record<string, unknown>) } as echarts.EChartsOption;
  next.xAxis = Array.isArray(option.xAxis)
    ? option.xAxis.map(patchAxis) as echarts.EChartsOption['xAxis']
    : patchAxis(option.xAxis) as echarts.EChartsOption['xAxis'];
  return next;
}

/** The `lib-waterfall-chart` widget data of a tile (bars + option + format flags). */
export function buildWaterfallTileData(tile: DashboardTile): Record<string, unknown> {
  return {
    type: 'waterfall-chart',
    title: tile.title,
    widgetId: `dvm-tile-waterfall-${tile.id}`,
    height: '100%',
    hideCardHeader: true,
    waterfallBars: tile.waterfallBars,
    option: tile.previewOption,
    format: tile.format,
    crossFilter: tile.format?.['crossFilter'] === true,
    highlightOnClick: tile.format?.['highlightOnClick'] === true,
    filterKey: tile.filterKey || undefined,
  };
}

/**
 * The default Generate flow's tile: the response bars (with the form's step
 * signs applied) plus the preview option and the published widget data. Also
 * the legacy fallback for a family without its own `tileFromResponse`.
 */
export function waterfallTileFromResponse(
  base: DashboardTile,
  response: Record<string, unknown> | null,
  payload: Record<string, unknown>,
  title: string | undefined,
): DashboardTile {
  const format = { ...(base.format || {}), title };
  const rawBars = (Array.isArray(response) ? response : (response as { data?: WaterfallBar[] } | null)?.data || []) as WaterfallBar[];
  const bars = applyStepSignsToWaterfallBars(rawBars, payload);
  const tile: DashboardTile = { ...base, format, waterfallBars: bars, previewOption: buildWaterfallPreviewOption(bars, format) };
  tile.waterfallData = buildWaterfallTileData(tile);
  return tile;
}
